import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "../lib/apiClient";
import {
  buildUploadFormData,
  type TaxDocumentUploadFormValues,
  type TaxDocumentUploadResponse,
} from "../schemas/taxDocumentUpload";

/**
 * UC-008 (Phase 22) tax document upload mutation hook.
 *
 * POST /api/admin/months/{budgetId}/tax-document  (multipart/form-data)
 *
 * apiCall expectations: response is JSON, success 201 / 200.
 * Multipart requires raw fetch (apiCall sends JSON body); proto bypass přímo.
 */
export function useUploadTaxDocument() {
  const client = useQueryClient();
  return useMutation<
    TaxDocumentUploadResponse,
    ApiError,
    { budgetId: string; tenantId: string; values: TaxDocumentUploadFormValues }
  >({
    mutationFn: async ({ budgetId, tenantId, values }) => {
      const fd = buildUploadFormData(values, tenantId);
      const csrf = readCsrfCookie();
      const headers: Record<string, string> = { Accept: "application/json" };
      if (csrf) headers["X-CSRF-Token"] = csrf;
      const response = await fetch(`/api/admin/months/${budgetId}/tax-document`, {
        method: "POST",
        credentials: "include",
        headers,
        body: fd,
      });
      if (response.ok) {
        return (await response.json()) as TaxDocumentUploadResponse;
      }
      const body = (await response.json().catch(() => ({}))) as { error_code?: string; message?: string };
      throw new ApiError(response.status, body);
    },
    onSuccess: (_, { budgetId }) => {
      void client.invalidateQueries({ queryKey: ["tax-document", budgetId] });
      void client.invalidateQueries({ queryKey: ["budget-detail", budgetId] });
    },
  });
}

function readCsrfCookie(): string | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(/(?:^|;\s*)axigon_csrf=([^;]+)/);
  return match?.[1] ?? null;
}
